import { useState } from 'react';
import { useMutation } from '@apollo/client';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Typography from '@mui/joy/Typography';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import Slider from '@mui/joy/Slider';
import Divider from '@mui/joy/Divider';
import Sheet from '@mui/joy/Sheet';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import { PURCHASE_ADDITIONAL_CREDITS } from '../../gql/subscription.mutations';
import { useFeedback } from '../feedback/Service';

export default function BuyCreditsModal({
  open,
  onClose,
  onSuccess,
  pricePerCredit = 0,
  currentCredits = 0,
  maxCredits = 100
}) {
  const { snackbar } = useFeedback();
  const [amount, setAmount] = useState(10);
  
  const [purchaseCredits, { loading }] = useMutation(PURCHASE_ADDITIONAL_CREDITS);

  const availableSpace = Math.max(0, maxCredits - currentCredits);
  const maxPurchase = availableSpace;
  const totalCost = (amount * pricePerCredit).toFixed(2);

  const handleAmountChange = (newValue) => {
    const clampedValue = Math.max(1, Math.min(newValue, maxPurchase));
    setAmount(clampedValue);
  };

  const handlePurchase = async () => {
    try {
      const { data } = await purchaseCredits({
        variables: { amount }
      });
      
      if (data?.purchaseAdditionalCredits?.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.purchaseAdditionalCredits.url;
      } else {
        onSuccess?.();
      }
    } catch (error) {
      snackbar({ text: error.message || 'Failed to initiate credit purchase', type: 'error' });
    }
  };

  const handleClose = () => {
    setAmount(10);
    onClose();
  };

  const presetAmounts = [5, 10, 25, 50];

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        sx={{
          maxWidth: 450,
          width: '100%',
          overflow: 'auto'
        }}
      >
        <ModalClose />
        
        <Typography level="h4" sx={{ mb: 1 }}>
          Buy Additional Credits
        </Typography>
        <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
          Credits are used for video interviews.
        </Typography>

        {/* Current Credits Display */}
        <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md', mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography level="body-sm" color="neutral">
              Current Credits
            </Typography>
            <Typography level="body-md" fontWeight="lg">
              {currentCredits} / {maxCredits}
            </Typography>
          </Box>
        </Sheet>

        {availableSpace <= 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography level="body-lg" color="warning">
              You{"'"}ve reached your maximum credit limit.
            </Typography>
            <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
              Upgrade your plan to increase your credit limit.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Quick Select Buttons */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
              {presetAmounts.filter(a => a <= maxPurchase).map((preset) => (
                <Button
                  key={preset}
                  size="sm"
                  variant={amount === preset ? 'solid' : 'outlined'}
                  onClick={() => setAmount(preset)}
                >
                  {preset} credits
                </Button>
              ))}
            </Box>

            {/* Amount Selector */}
            <Box sx={{ mb: 3 }}>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Custom Amount
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={() => handleAmountChange(amount - 1)}
                  disabled={amount <= 1}
                >
                  <Remove />
                </Button>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => handleAmountChange(parseInt(e.target.value) || 1)}
                  slotProps={{
                    input: {
                      min: 1,
                      max: maxPurchase,
                      style: { textAlign: 'center' }
                    }
                  }}
                  sx={{ width: 100 }}
                />
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={() => handleAmountChange(amount + 1)}
                  disabled={amount >= maxPurchase}
                >
                  <Add />
                </Button>
              </Box>
            </Box>

            {/* Slider */}
            <Box sx={{ mb: 3, px: 1 }}>
              <Slider
                value={amount}
                onChange={(_, value) => setAmount(value)}
                min={1}
                max={maxPurchase}
                step={1}
                marks={[
                  { value: 1, label: '1' },
                  { value: maxPurchase, label: String(maxPurchase) }
                ]}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Summary */}
            <Sheet variant="soft" color="primary" sx={{ p: 2, borderRadius: 'md', mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography level="body-sm">
                  {amount} credits × ${pricePerCredit}
                </Typography>
                <Typography level="body-md" fontWeight="lg">
                  ${totalCost}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography level="body-sm" color="neutral">
                  After purchase
                </Typography>
                <Typography level="body-sm">
                  {currentCredits + amount} / {maxCredits} credits
                </Typography>
              </Box>
            </Sheet>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="plain" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handlePurchase}
                loading={loading}
                disabled={amount <= 0 || amount > maxPurchase}
              >
                Pay ${totalCost}
              </Button>
            </Box>
          </>
        )}
      </ModalDialog>
    </Modal>
  );
}
